import { useState, useEffect, useCallback, useRef } from 'react'
import { Smartphone, Youtube, GripVertical, LayoutGrid } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import './RokuSettings.css' // shared rs-* design-system styles

// Apple Mobile (iOS) app runtime settings — managed in Vader, delivered to the iOS app via
// watch /api/config → `apple` block. Source of truth is Vader (app_settings D1 k/v).
//   • apple_youtube_mode : 'handoff' | 'in_app'  (how the app opens a live YouTube stream)
//   • apple_menu         : JSON string { tabs:[{id,visible}] } — iOS bottom tab-bar order + visibility.
//                          Array order = display order. Delivered parsed as apple.menu. See CH-20260807-02.

// iOS tab identities — MUST match ContentView.AppTab rawValues. Display labels are UI-only; the wire
// value is always the `id`.
const TAB_LABELS = { home: 'Home', guide: 'Guide', vod: 'On Demand', search: 'Search', channels: 'Channels', live: 'Live' }
const VALID_IDS = Object.keys(TAB_LABELS)

// Default / "Reset" order = iOS AppTab.defaultOrder (matches today's ship build), all visible.
const DEFAULT_MENU = [
  { id: 'home', visible: true },
  { id: 'guide', visible: true },
  { id: 'vod', visible: true },
  { id: 'live', visible: true },
  { id: 'channels', visible: true },
  { id: 'search', visible: true },
]

// Parse a stored apple_menu string → normalized ordered array (always all six ids; missing → hidden).
// Returns null on empty/invalid so callers fall back to DEFAULT_MENU in the editor.
function parseMenu(raw) {
  try {
    const obj = typeof raw === 'string' ? JSON.parse(raw) : raw
    if (!obj || !Array.isArray(obj.tabs)) return null
    const seen = new Set()
    const tabs = []
    for (const t of obj.tabs) {
      if (!t || !VALID_IDS.includes(t.id) || seen.has(t.id)) continue
      seen.add(t.id)
      tabs.push({ id: t.id, visible: t.visible !== false })
    }
    for (const id of VALID_IDS) if (!seen.has(id)) tabs.push({ id, visible: false }) // show all six in the editor
    return tabs.length ? tabs : null
  } catch { return null }
}

export default function AppleMobileSettings() {
  const { api } = useAuth()
  const [ytMode, setYtMode] = useState('handoff')
  const [menu, setMenu] = useState(DEFAULT_MENU)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [status, setStatus] = useState('')
  const dragIndex = useRef(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await api('/settings?prefix=apple_')
      const s = data.settings || {}
      setYtMode(s.apple_youtube_mode === 'in_app' ? 'in_app' : 'handoff')
      setMenu(parseMenu(s.apple_menu) || DEFAULT_MENU)
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    } finally {
      setLoading(false)
    }
  }, [api])

  useEffect(() => { load() }, [load])

  const visibleCount = menu.filter(t => t.visible).length
  const menuValid = visibleCount >= 1

  function toggleVisible(id) {
    setMenu(m => m.map(t => (t.id === id ? { ...t, visible: !t.visible } : t)))
  }

  // Native HTML5 drag-to-reorder (no dependency).
  function onDragStart(i) { dragIndex.current = i }
  function onDragOver(e, i) {
    e.preventDefault()
    const from = dragIndex.current
    if (from === null || from === i) return
    setMenu(m => {
      const next = [...m]
      const [moved] = next.splice(from, 1)
      next.splice(i, 0, moved)
      return next
    })
    dragIndex.current = i
  }
  function onDragEnd() { dragIndex.current = null }

  async function persist(nextYt, nextMenu) {
    setSaving(true); setStatus('Saving…')
    try {
      const payload = {
        apple_youtube_mode: nextYt,
        apple_menu: JSON.stringify({ tabs: nextMenu }), // stored as a JSON string; emitted parsed
      }
      await api('/settings', { method: 'PUT', body: JSON.stringify({ settings: payload }) })
      setStatus('✅ Saved. The iOS app reflects changes within ~5 min (/api/config cache).')
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    } finally {
      setSaving(false)
    }
  }

  function save() { if (menuValid) persist(ytMode, menu) }
  function resetMenu() {
    setMenu(DEFAULT_MENU)
    persist(ytMode, DEFAULT_MENU) // Reset writes the all-visible default blob immediately
  }

  const rowStyle = {
    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
    border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, marginBottom: 6,
    background: 'rgba(255,255,255,0.03)', cursor: 'grab',
  }

  return (
    <div className="roku-settings">
      <div className="roku-settings-header">
        <h1><Smartphone size={22} /> Apple Mobile Settings</h1>
        <span className="status-text">{status}</span>
      </div>

      {/* ── Live YouTube option ─────────────────────────────────────────────── */}
      <div className="rs-card">
        <h2><Youtube size={18} /> Live YouTube option</h2>
        <p className="rs-hint">
          When a live event offers a YouTube stream (<code>youtubeLiveAvailable</code>), choose how the iOS app
          plays it. Delivered via watch <code>/api/config</code> → <code>apple.youtube_mode</code>; the offer is
          always user-initiated (never auto-play).
        </p>
        {loading ? <div className="rs-loading">Loading…</div> : (
          <div className="rs-field">
            <div className="rs-tabs" role="radiogroup" aria-label="YouTube mode">
              <button type="button" role="radio" aria-checked={ytMode === 'handoff'}
                className={`rs-tab${ytMode === 'handoff' ? ' active' : ''}`}
                onClick={() => setYtMode('handoff')}>
                Hand-off (open YouTube)
              </button>
              <button type="button" role="radio" aria-checked={ytMode === 'in_app'}
                className={`rs-tab${ytMode === 'in_app' ? ' active' : ''}`}
                onClick={() => setYtMode('in_app')}>
                In-app (WKWebView embed)
              </button>
            </div>
            <em className="rs-sub" style={{ marginTop: 10 }}>
              {ytMode === 'handoff'
                ? 'Hand-off: tapping “Watch on YouTube” leaves the app (YouTube app / Safari). Matches web, Fire TV and Roku. The TBTV Mux/analytics session ends when the user leaves.'
                : 'In-app: YouTube plays inside the app in a WKWebView embed (keeps the user in-app). iOS-only — the other apps always hand off.'}
            </em>
          </div>
        )}
      </div>

      {/* ── Tab Bar (iOS) ───────────────────────────────────────────────────── */}
      <div className="rs-card">
        <h2><LayoutGrid size={18} /> Tab Bar (iOS)</h2>
        <p className="rs-hint">
          Order + visibility of tabs in the iOS bottom bar. Drag to reorder. Toggle to show/hide. The first
          4 visible tabs render in the bar; any extras go into iOS's <strong>More</strong> overflow.
        </p>
        {loading ? <div className="rs-loading">Loading…</div> : (
          <div className="rs-field">
            <div>
              {menu.map((t, i) => (
                <div key={t.id} style={rowStyle} draggable
                  onDragStart={() => onDragStart(i)}
                  onDragOver={(e) => onDragOver(e, i)}
                  onDragEnd={onDragEnd}
                  title="Drag to reorder">
                  <span style={{ color: 'rgba(255,255,255,0.4)', display: 'flex' }} aria-hidden="true">
                    <GripVertical size={16} />
                  </span>
                  <span style={{ flex: 1, fontWeight: 600 }}>
                    {TAB_LABELS[t.id]} <code style={{ opacity: 0.5, fontWeight: 400 }}>{t.id}</code>
                  </span>
                  <button type="button" role="switch" aria-checked={t.visible}
                    className={`rs-tab${t.visible ? ' active' : ''}`}
                    style={{ minWidth: 84 }}
                    onClick={() => toggleVisible(t.id)}>
                    {t.visible ? 'Visible' : 'Hidden'}
                  </button>
                </div>
              ))}
            </div>
            <em className="rs-sub" style={{ marginTop: 6, color: menuValid ? undefined : '#dc2626' }}>
              {menuValid
                ? `${visibleCount} visible. Order above = tab-bar order on iOS.`
                : '⚠ At least one tab must be visible — Save is disabled.'}
            </em>
          </div>
        )}
      </div>

      {/* ── Save / Reset ────────────────────────────────────────────────────── */}
      <div className="rs-card">
        <div className="rs-field" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button className="cmpl-btn cmpl-btn-primary" onClick={save} disabled={saving || loading || !menuValid}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button className="cmpl-btn" onClick={resetMenu} disabled={saving || loading}>
            Reset tab bar to default
          </button>
        </div>
      </div>
    </div>
  )
}
